import { RequestConfig } from 'tree-wallet-client';
import { getToken } from '../Service/AuthService';
import TokenExpiredException from '../Exception/TokenExpiredException';
import HttpErrorException from '../Exception/HttpErrorException';
import NetworkConnectivityException from '../Exception/NetworkConnectivityException';
import { getImpersonationData, isImpersonating } from '../Service/RoleService';

interface ParsedResponse {
  // parsed JSON payload, or null when the body was empty or not JSON at all
  data: any;
  // trimmed raw body, kept to build a usable message out of non JSON answers
  raw: string;
}

export const headersConstants = {
  impersonation: 'X-Switch-User',
  authorization: 'Authorization',
  contentType: 'Content-Type',
};


export async function request(url: string, config?: RequestConfig) {
  const getImpersonationHeader = () => {
    if (isImpersonating()) {
      const impersonationData = getImpersonationData();
      if (!impersonationData || !impersonationData['user']) {
        throw new Error('missing impersonated user');
      }
      return { [headersConstants.impersonation]: impersonationData['user'] };
    }
    return {};
  }

  const fetchConfig = config && {
    ...config?.httpConfig,
    headers: {
      ...(config?.httpConfig?.multipart ? {} : { [headersConstants.contentType]: 'application/json' }),
      ...(config?.httpConfig?.contentType ? config?.httpConfig?.contentType : {}),
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      ...config?.httpConfig?.headers,
      ...(getImpersonationHeader()),
    },
    ...(config.controller ? { signal: config.controller.signal } : {})
  };

  const queryString = String(new URLSearchParams({ ...config?.queryParams }));
  const separator = url.includes('?') ? '&' : '?';
  const fullUrl = url + (queryString ? separator + queryString : '');

  let responseBody: any;

  try {
    let response: Response;

    try {
      response = await fetch(fullUrl, fetchConfig);
    } catch (error) {
      // The caller no longer wants the answer (Back while a country payload is downloading,
      // leaving a page mid request): an aborted request is not a failure. Returning here keeps
      // it out of errorCallback and out of the persisted console logger - the caller is the one
      // who cancelled it, and it already knows.
      if (error?.name === 'AbortError') {
        return;
      }

      // fetch() only rejects when the request never reached the server: offline,
      // DNS/TLS failure, blocked by CORS, aborted. Anything the server answers -
      // including 4xx and 5xx - resolves and is handled below.
      throw new NetworkConnectivityException(error?.message || 'the request could not be sent');
    }

    const parsedResponse = await readResponseBody(response);

    if (!response.ok) {
      processFetchError(response, parsedResponse);
    }

    responseBody = parsedResponse.data;
  } catch (error) {
    // fetchConfig is deliberately not logged: it carries the bearer token, and
    // console.error is persisted to local storage by the app logger.
    console.error('There was an error while processing request', {
      url: fullUrl,
      name: error?.name,
      status: error?.status,
      message: error?.message,
    });
    config && config.errorCallback && config.errorCallback(error);
    return;
  }

  // Called outside of the try/catch on purpose: an exception raised by the
  // caller's own success handler is a bug in that handler, not a failed request.
  // Reporting it through errorCallback made successful writes look like network
  // errors and kept them in the offline queue forever.
  if (config && config.successCallback) {
    config.successCallback(responseBody);
  }

  return responseBody;
}

/**
 * Reads the body once, as text, and parses it defensively.
 *
 * response.json() cannot be used directly: it rejects on an empty body (a 204,
 * or a 201 with no payload) and on the HTML error pages the API serves for
 * uncaught exceptions and unknown routes. In WebKit that rejection is a bare
 * SyntaxError whose message is "The string did not match the expected pattern.",
 * which is how a plain server error ended up being shown to planters as a
 * network problem on Safari and iOS.
 */
async function readResponseBody(response: Response): Promise<ParsedResponse> {
  let rawBody = '';

  try {
    rawBody = await response.text();
  } catch (error) {
    console.warn('Could not read response body', { status: response.status, message: error?.message });
    return { data: null, raw: '' };
  }

  const raw = rawBody.trim();

  if (!raw) {
    return { data: null, raw };
  }

  try {
    return { data: JSON.parse(raw), raw };
  } catch (error) {
    if (response.ok) {
      // A successful write must not be undone by an unparsable payload: no
      // caller needs the body to consider the request done.
      console.warn('Response was not JSON, ignoring the body', {
        status: response.status,
        contentType: response.headers.get('content-type'),
        snippet: raw.substring(0, 200),
      });
    }
    return { data: null, raw };
  }
}

function processFetchError(response: Response, parsedResponse: ParsedResponse) {
  const errorText = buildErrorMessage(response, parsedResponse);

  // The real HTTP status is used here. The previous version read `status` from
  // the payload, where this API returns either the string "403 Forbidden" or a
  // `code` property, so the expired token case was never detected.
  if (401 === response.status || 403 === response.status) {
    throw new TokenExpiredException(errorText);
  }

  throw new HttpErrorException(errorText, response.status, parsedResponse.data);
}

function buildErrorMessage(response: Response, { data, raw }: ParsedResponse) {
  const serverMessage = data && (data.message || data.error || data.detail || data['hydra:description']);

  if (serverMessage) {
    return `${response.status} - ${serverMessage}`;
  }

  // HTML error pages (Symfony, nginx) still carry a readable reason in the title
  const htmlTitle = raw.match(/<title>([^<]*)<\/title>/i);

  if (htmlTitle) {
    return `${response.status} - ${htmlTitle[1].trim()}`;
  }

  return `${response.status} - ${response.statusText || 'request failed'}`;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getHeaders(config?: RequestConfig) {
  let disableAuth = false;
  const headers = new Headers();
  const defaultHeaders = {
    'Sec-Fetch-Dest': 'empty',
    'Sec-Fetch-Mode': 'cors',
    'Sec-Fetch-Site': 'same-site',
    'Sec-GPC': '1',
    'Accept': '*/*',
    'Accept-Encoding': 'gzip, deflate, br',
    'Accept-Language': 'fr-FR,fr',
  }

  for (const header of Object.entries(defaultHeaders)) {
    const [headerName, headerValue] = header;
    headers.append(headerName, headerValue);
  }

  if (config) {
    if (config.httpConfig?.headers) {
      for (const headerName in config.httpConfig?.headers) {
        const headerValue = config.httpConfig?.headers[headerName];
        if (headers.has(headerName)) {
          headers.set(headerName, headerValue);
        } else {
          headers.append(headerName, headerValue);
        }
      }
    }

    if (!config.httpConfig?.multipart) {
      headers.set(headersConstants.contentType, 'application/json');
    }

    if (config.httpConfig?.contentType) {
      headers.set(headersConstants.contentType, config.httpConfig?.contentType);
    } else {
      headers.set(headersConstants.contentType, 'application/json');
    }

    if (config.disableAuth) {
      disableAuth = config.disableAuth;
    }
  }

  if (getToken() && !disableAuth) {
    headers.set(headersConstants.authorization, `Bearer ${getToken()}`);
  }

  if (isImpersonating()) {
    const impersonationData = getImpersonationData();
    if (!impersonationData || !impersonationData['user']) {
      throw new Error('missing impersonated user');
    }
    headers.set(headersConstants.impersonation, impersonationData['user']);
  }

  return headers;
}